import { useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { JOURNAL_HISTORY_WIDTH_STEP } from '@/lib/journalWritePrefs';
import { cn } from '@/utils/cn';

export function JournalHistoryResizeHandle({
  value,
  min,
  max,
  label,
  onChange,
  onReset,
}: {
  value: number;
  min: number;
  max: number;
  label: string;
  onChange: (width: number) => void;
  onReset: () => void;
}) {
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(value);
  const [active, setActive] = useState(false);

  function clamp(width: number): number {
    return Math.min(max, Math.max(min, Math.round(width)));
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    dragging.current = true;
    startX.current = event.clientX;
    startWidth.current = value;
    setActive(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging.current) return;
    onChange(clamp(startWidth.current + (event.clientX - startX.current)));
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging.current) return;
    dragging.current = false;
    setActive(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        onChange(clamp(value + JOURNAL_HISTORY_WIDTH_STEP));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        onChange(clamp(value - JOURNAL_HISTORY_WIDTH_STEP));
        break;
      default:
        break;
    }
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      tabIndex={0}
      className={cn('jw-resize', active && 'is-drag')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={(event) => {
        event.preventDefault();
        onReset();
      }}
    />
  );
}
